import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { clientsApi, servicePlansApi } from '../api/endpoints';
import type { BulkImportResult } from '../api/types';
import { ApiError } from '../api/client';
import { useToast } from '../app/ToastProvider';

interface Props { open: boolean; initialStep?: 0 | 1; onClose: () => void; }
const steps = ['Upload service plans', 'Upload clients'];

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The file could not be read.'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(file);
  });
}

export function BulkImportDialog({ open, initialStep = 0, onClose }: Props) {
  const [activeStep, setActiveStep] = useState<number>(initialStep);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  useEffect(() => { if (open) { setActiveStep(initialStep); setFile(null); setResult(null); setError(null); } }, [open, initialStep]);

  const mutation = useMutation({
    mutationFn: async (dryRun: boolean) => {
      if (!file) throw new Error('Choose a CSV or Excel file first.');
      const payload = { fileName: file.name, dataBase64: await readBase64(file), dryRun };
      return activeStep === 0 ? servicePlansApi.import(payload) : clientsApi.import(payload);
    },
    onSuccess: async ({ data, message }, dryRun) => {
      setResult(data); setError(null);
      if (!dryRun && data.importedRows > 0) {
        toast.success(message);
        await queryClient.invalidateQueries({ queryKey: [activeStep === 0 ? 'service-plans' : 'clients'] });
        if (activeStep === 0) { setActiveStep(1); setFile(null); setResult(null); }
      }
    },
    onError: (caught) => setError(caught instanceof ApiError || caught instanceof Error ? caught.message : 'The file could not be processed.'),
  });

  const selectStep = (step: number) => { setActiveStep(step); setFile(null); setResult(null); setError(null); };
  const valid = result && result.errors.length === 0 && result.validRows > 0;
  const csvName = activeStep === 0 ? 'service-plans-template.csv' : 'clients-template.csv';
  const templateBase = import.meta.env.BASE_URL.endsWith('/') ? import.meta.env.BASE_URL : import.meta.env.BASE_URL + '/';

  return <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
    <DialogTitle>Bulk import</DialogTitle>
    <DialogContent dividers>
      <Stepper nonLinear activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label, index) => <Step key={label}><StepButton onClick={() => selectStep(index)}>{label}</StepButton></Step>)}
      </Stepper>
      {activeStep === 1 ? <Alert severity="info" sx={{ mb: 2 }}>Import service plans first. Client rows use <strong>service_plan_code</strong> to attach subscriptions.</Alert> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Stack spacing={2}>
        <Typography variant="body2">Download a template, keep the header row unchanged, and add one record per row.</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            {file ? file.name : 'Choose CSV or Excel file'}
            <input hidden type="file" accept=".csv,.xlsx" onChange={(event) => { setFile(event.target.files?.[0] ?? null); setResult(null); setError(null); }} />
          </Button>
          <Button onClick={() => mutation.mutate(true)} disabled={!file || mutation.isPending}>Validate file</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Templates: <Link href={templateBase + 'templates/' + csvName} download>{activeStep === 0 ? 'Service Plans CSV' : 'Clients CSV'}</Link>
          {' · '}<Link href={templateBase + 'templates/bulk-import-template.xlsx'} download>Excel workbook (both sheets)</Link>
        </Typography>
        {result ? <>
          <Divider />
          <Alert severity={result.errors.length ? 'warning' : 'success'}>
            {result.errors.length ? result.errors.length + ' issue(s) found. Fix the listed rows and validate again.' : result.validRows + ' row(s) are ready to import.'}
          </Alert>
          {result.errors.length ? <Table size="small"><TableHead><TableRow><TableCell>Row</TableCell><TableCell>Column</TableCell><TableCell>Issue</TableCell></TableRow></TableHead>
            <TableBody>{result.errors.slice(0, 100).map((item, index) => <TableRow key={index}><TableCell>{item.row}</TableCell><TableCell>{item.field}</TableCell><TableCell>{item.message}</TableCell></TableRow>)}</TableBody></Table> : null}
        </> : null}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="inherit" disabled={mutation.isPending}>Close</Button>
      <Button variant="contained" onClick={() => mutation.mutate(false)} disabled={!valid || mutation.isPending}>
        {activeStep === 0 ? 'Import plans and continue' : 'Import clients'}
      </Button>
    </DialogActions>
  </Dialog>;
}
